import React, {
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useState,
} from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled, { ThemeProvider } from 'styled-components';
import { ToastContainer } from 'react-toastify';
import JobList from 'pages/jobs/JobList';
import Login from 'pages/auth/Login';
import SignUp from 'pages/auth';
import JobPostForm from 'pages/jobs';
import Activity from 'pages/home';
import Layout from 'pages/layout';
import Profiles from 'pages/profile';
import AddProfile from 'pages/addprofile';
import LightTheme from 'assert/LightTheme';
import DarkTheme from 'assert/DarkTheme';
import ResetPassword from 'pages/auth/ResetPassword';
import { changeMode } from 'redux/features/LoginSlice';
import JobdetailView from 'pages/jobs/JobDetailView';
import Network from 'pages/network';
import Posthistory from 'pages/home/PostHistory';
import BuinessPlace from 'pages/business';
import Settings from 'pages/settings';
import GlobalStyle from 'GlobalStyle';
import NotFound from 'pages/layout/NotFound';
import JobActivity from 'pages/jobactivity';
import JobAppliedTab from 'pages/jobactivity/JobAppliedTab';
import DetailView from 'pages/business/DetailView';
import PostList from 'pages/home/PostList';
import More from 'pages/settings/More';
import ProjectInfo from 'pages/Info';
import ProfileBusinessList from 'pages/business/ProfileBusinessList';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import FindJobsBlog from 'pages/blogs/FindJobsBlog';
import BlogLayout from 'pages/blogs/BlogLayout';
import GrowNetworkBlog from 'pages/blogs/GrowNetworkBlog';
import PromoteBusinessBlog from 'pages/blogs/PromoteBusinessBlog';
import BlogHome from 'pages/blogs/BlogHome';
import Search from 'pages/search';
import Notification from 'pages/Notification';
import { AuthContext } from './AuthProvider';
import PublicRoute from './PublicRoute';
import ProtectedRoute from 'router/Producted';
import { BrandIconSpinner } from 'components';
import BeforeLoginLayout from 'pages/layout/BeforeLoginLayout';
import UserResume from 'pages/profile/UserResume';
import ShareResume from 'pages/profile/ShareResume';
// import { enableAutoScrollOnFocus } from 'utils/enableAutoScrollOnFocus';
import DeepLinkHandler from 'utils/handleAppDeepLink';
import AboutUs from 'pages/privacy/AboutUs';
import PrivacyPolicy from 'pages/privacy/PrivacyPolicy';
import TermsAndCondition from 'pages/privacy/TermsAndCondition';
import SafetyStandard from 'pages/privacy/SafetyStandard';
import Landing from 'pages/layout/Landing';
// import StickyLangChange from 'lang/stickyLangChange';

const PageLoader = styled.div`
  background-color: ${({ theme }) => theme.Bg1};
  height: 100vh;
  display: grid;
  align-items: center;
  justify-content: center;
  > div {
    background-color: inherit;
  }
`;

export default function Router() {
  const dispatch = useDispatch();
  const { getTheme } = useSelector((state) => state?.LoginReducer);
  const { isLoggedIn, isAuthReady } = useContext(AuthContext);
  const [theme, setTheme] = React.useState('');
  const [isIntitalLoad, setIsIntitalLoad] = useState(true);

  useEffect(() => {
    const savedMode = localStorage.getItem('mode');
    if (getTheme !== '' && savedMode) {
      setTheme(savedMode);
    } else {
      setTheme(getTheme || 'light');
    }
  }, [getTheme]);

  // useEffect(() => {
  //   const cleanup = enableAutoScrollOnFocus();
  //   return cleanup;
  // }, []);

  const handleApplyStatusBar = async () => {
    if (Capacitor.getPlatform() === 'android') {
      await EdgeToEdge.setBackgroundColor({
        color: theme === 'Dark' ? '#131314' : '#ffffff',
      });
    }
    await StatusBar.setStyle({
      style: theme === 'Dark' ? Style.Dark : Style.Light,
    });
  };

  useEffect(() => {
    if (
      isAuthReady &&
      theme &&
      (Capacitor.getPlatform() === 'android' ||
        Capacitor.getPlatform() === 'ios')
    ) {
      if (isIntitalLoad) {
        setIsIntitalLoad(false);
        setTimeout(() => {
          handleApplyStatusBar();
        }, 2000);
      } else {
        handleApplyStatusBar();
      }
    }
  }, [theme, isAuthReady]);

  const memoizedDispatch = useCallback(() => {
    dispatch(changeMode(localStorage.getItem('mode')));
  }, [dispatch]);

  useEffect(() => {
    memoizedDispatch();
  }, [memoizedDispatch]);

  const currentTheme = useMemo(
    () => (theme === 'Dark' ? DarkTheme : LightTheme),
    [theme]
  );

  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyle />
      {!isAuthReady ? (
        <PageLoader>
          <BrandIconSpinner loading={true} />
        </PageLoader>
      ) : (
        <>
          <BrowserRouter forceRefresh={true}>
            <DeepLinkHandler />
            <Routes>
              {/* Public Auth Routes */}
              <Route
                path="/landing"
                element={
                  <PublicRoute>
                    <Landing />
                  </PublicRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUp />
                  </PublicRoute>
                }
              />
              <Route
                path="/resetpassword"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/version"
                element={
                  <PublicRoute>
                    <ProjectInfo />
                  </PublicRoute>
                }
              />

              <Route
                path="/about-us"
                element={
                  <BlogLayout>
                    <AboutUs hideBackArrow isAuthPage />
                  </BlogLayout>
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <BlogLayout>
                    <PrivacyPolicy hideBackArrow isAuthPage />
                  </BlogLayout>
                }
              />
              <Route
                path="/terms"
                element={
                  <BlogLayout>
                    <TermsAndCondition hideBackArrow isAuthPage />
                  </BlogLayout>
                }
              />
              <Route
                path="/safety-standard"
                element={
                  <BlogLayout>
                    <SafetyStandard hideBackArrow isAuthPage />
                  </BlogLayout>
                }
              />

              {/* Blog Routes (public) */}
              <Route path="/blog" element={<BlogLayout />}>
                <Route index element={<BlogHome />} />
                <Route
                  path="find-jobs-online-easily-with-wafynet"
                  element={<FindJobsBlog />}
                />
                <Route
                  path="grow-your-professional-network-online"
                  element={<GrowNetworkBlog />}
                />
                <Route
                  path="promote-your-business-online"
                  element={<PromoteBusinessBlog />}
                />
              </Route>
              <Route element={<BlogLayout hideBackArrow />}>
                <Route
                  path="/r/:username/:resumeid"
                  element={<ShareResume />}
                />
              </Route>

              <Route
                path="/"
                element={
                  isLoggedIn ? (
                    <ProtectedRoute>
                      <Layout>
                        <Activity />
                      </Layout>
                    </ProtectedRoute>
                  ) : (
                    <PublicRoute>
                      <BeforeLoginLayout>
                        <Activity />
                      </BeforeLoginLayout>
                    </PublicRoute>
                  )
                }
              />

              <Route
                path="/jobs"
                element={
                  isLoggedIn ? (
                    <ProtectedRoute>
                      <Layout>
                        <JobList />
                      </Layout>
                    </ProtectedRoute>
                  ) : (
                    <PublicRoute>
                      <BeforeLoginLayout>
                        <JobList />
                      </BeforeLoginLayout>
                    </PublicRoute>
                  )
                }
              />

              <Route
                path="/business-hub"
                element={
                  isLoggedIn ? (
                    <ProtectedRoute>
                      <Layout>
                        <BuinessPlace />
                      </Layout>
                    </ProtectedRoute>
                  ) : (
                    <PublicRoute>
                      <BeforeLoginLayout>
                        <BuinessPlace />
                      </BeforeLoginLayout>
                    </PublicRoute>
                  )
                }
              />

              {/* ProtectedRoute Routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/following"
                  element={
                    <ProtectedRoute>
                      <Activity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs-detail/:id"
                  element={
                    <ProtectedRoute>
                      <JobdetailView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/postjob"
                  element={
                    <ProtectedRoute>
                      <JobPostForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/business-hub/detail/:id"
                  element={
                    <ProtectedRoute>
                      <DetailView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/post-details/:postId"
                  element={
                    <ProtectedRoute>
                      <PostList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/:username"
                  element={
                    <ProtectedRoute>
                      <Profiles />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/home/:tabname"
                  element={
                    <ProtectedRoute>
                      <AddProfile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:tabname"
                  element={
                    <ProtectedRoute>
                      <JobActivity />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/applied-list"
                  element={
                    <ProtectedRoute>
                      <JobAppliedTab />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/:tab"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/network/:tabname"
                  element={
                    <ProtectedRoute>
                      <Network />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/search/:tabname"
                  element={
                    <ProtectedRoute>
                      <Search />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/post-history/:postId"
                  element={
                    <ProtectedRoute>
                      <Posthistory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/more"
                  element={
                    <ProtectedRoute>
                      <More />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile/business-hub"
                  element={
                    <ProtectedRoute>
                      <ProfileBusinessList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notification"
                  element={
                    <ProtectedRoute>
                      <Notification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notification/:tabname"
                  element={
                    <ProtectedRoute>
                      <Notification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/resume/:username"
                  element={
                    <ProtectedRoute>
                      <UserResume />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* 404 - Show only after auth check */}
              <Route path="*" element={<NotFound isLoggedIn={isLoggedIn} />} />
            </Routes>
          </BrowserRouter>
          <ToastContainer />
          {/* <StickyLangChange /> */}
        </>
      )}
    </ThemeProvider>
  );
}
